import { Component, ViewEncapsulation, OnInit, OnDestroy, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatChipsModule } from '@angular/material/chips';
import { FuseDrawerComponent, FuseDrawerService } from '@fuse/components/drawer';
import { Member } from 'app/modules/members/members.types';
import { Subject, takeUntil } from 'rxjs';
import { BasicPlayerType, PlayerAffiliation, PlayerSlot, PlayerTypeSelectionData } from '../booking.types';
import { BookingStateService } from '../booking-state.service';
import { PriceService } from 'app/modules/prices/price.service';

interface PlayerTypeGrouping {
    affiliation: PlayerAffiliation;
    playerTypes: BasicPlayerType[];
    isEnabled: boolean;
    headerText: string;
    headerDescription: string;
    color: string;
    icon: string;
}

@Component({
    selector: 'player-type-drawer',
    templateUrl: './player-type-drawer.component.html',
    styles: [
        `
            player-type-drawer {
                position: static;
                display: block;
                flex: none;
                width: auto;
            }
        `,
    ],
    encapsulation: ViewEncapsulation.None,
    imports: [
        CommonModule,
        MatIconModule,
        FuseDrawerComponent,
        MatButtonModule,
        MatTooltipModule,
        MatCardModule,
        MatDividerModule,
        MatChipsModule,
    ],
    providers: [
        PriceService,
    ]
})
export class PlayerTypeDrawerComponent implements OnInit, OnDestroy {
    availablePlayerTypes: BasicPlayerType[] = [];
    currentCourseId: number | null = null;
    userMemberships: Member[] = [];
    playerIndex: number = 0;
    selectedPlayerSlot: PlayerSlot & { index: number; } | null = null;

    PlayerAffiliation = PlayerAffiliation;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _bookingStateService: BookingStateService,
        private _fuseDrawerService: FuseDrawerService,
        private _priceService: PriceService,
    ) {
        effect(() => {
            this.availablePlayerTypes = this._bookingStateService.course()?.playerTypes;
            this.currentCourseId = this._bookingStateService.course()?.id;
            this.userMemberships = this._bookingStateService.selectedPlayerSlot()?.members;
            this.playerIndex = this._bookingStateService.selectedPlayerSlot()?.index;
            this.selectedPlayerSlot = this._bookingStateService.selectedPlayerSlot();

            if (this.selectedPlayerSlot?.type === 'GUEST') {
                this.availablePlayerTypes = this.availablePlayerTypes.filter(pt => pt.affiliation === PlayerAffiliation.Public);
            }
        });
    }

    ngOnInit(): void {
        // Subscribe to selection data

    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    get isMemberAtCurrentCourse(): boolean {
        if (!this.currentCourseId || !this.userMemberships) return false;
        return this.userMemberships.some(member =>
            member.courseId === this.currentCourseId
        );
    }

    get isMemberAtOtherCourse(): boolean {
        if (!this.userMemberships) return false;
        return this.userMemberships.some(member =>
            member.courseId !== this.currentCourseId
        );
    }

    get playerTypeGroups(): PlayerTypeGrouping[] {
        const groupedTypes = this.groupPlayerTypesByAffiliation();
        const groups: PlayerTypeGrouping[] = [];

        // Define the order of affiliations
        const affiliationOrder = [
            PlayerAffiliation.Member,
            PlayerAffiliation.NonAffiliated,
            PlayerAffiliation.Public
        ];

        affiliationOrder.forEach(affiliation => {
            const playerTypes = groupedTypes[affiliation];
            if (playerTypes && playerTypes.length > 0) {
                groups.push({
                    affiliation,
                    playerTypes,
                    isEnabled: this.isAffiliationEnabled(affiliation),
                    headerText: this.getHeaderText(affiliation),
                    headerDescription: this.getHeaderDescription(affiliation),
                    color: this.getPlayerTypeColor(affiliation),
                    icon: this.getPlayerTypeIcon(affiliation)
                });
            }
        });

        return groups;
    }

    get membershipStatusText(): string {
        if (this.isMemberAtCurrentCourse) {
            return 'Member at this course - All rates available';
        } else if (this.isMemberAtOtherCourse) {
            return 'Member at another course - Affiliated and public rates available';
        } else {
            return 'Choose the appropriate player type to calculate the correct price';
        }
    }

    get membershipStatusColor(): string {
        if (this.isMemberAtCurrentCourse) {
            return 'text-green-600'; // Green for current course member
        } else if (this.isMemberAtOtherCourse) {
            return 'text-orange-600'; // Orange for other course member
        } else {
            return 'text-gray-600'; // Gray for public
        }
    }

    private groupPlayerTypesByAffiliation(): Record<PlayerAffiliation, BasicPlayerType[]> {
        const grouped: Record<PlayerAffiliation, BasicPlayerType[]> = {
            [PlayerAffiliation.Member]: [],
            [PlayerAffiliation.NonAffiliated]: [],
            [PlayerAffiliation.Public]: []
        };

        this.availablePlayerTypes?.forEach(playerType => {
            if (grouped[playerType.affiliation] !== undefined) {
                grouped[playerType.affiliation].push(playerType);
            }
        });

        return grouped;
    }

    private isAffiliationEnabled(affiliation: PlayerAffiliation): boolean {
        switch (affiliation) {
            case PlayerAffiliation.Member:
                // Member rates only available if member at current course
                return this.isMemberAtCurrentCourse;
            case PlayerAffiliation.NonAffiliated:
                // Affiliated rates available if member at current course OR member at other course
                return this.isMemberAtCurrentCourse || this.isMemberAtOtherCourse;
            case PlayerAffiliation.Public:
                // Public rates always available
                return true;
            default:
                return false;
        }
    }

    private getHeaderText(affiliation: PlayerAffiliation): string {
        switch (affiliation) {
            case PlayerAffiliation.Member:
                return 'Members';
            case PlayerAffiliation.NonAffiliated:
                return 'Affiliated';
            case PlayerAffiliation.Public:
                return 'Non-Affiliated';
            default:
                return '';
        }
    }

    private getHeaderDescription(affiliation: PlayerAffiliation): string {
        switch (affiliation) {
            case PlayerAffiliation.Member:
                return 'Golf club members with membership benefits';
            case PlayerAffiliation.NonAffiliated:
                return 'Has a membership at another club';
            case PlayerAffiliation.Public:
                return 'General public players';
            default:
                return '';
        }
    }

    getPlayerTypeColor(affiliation: PlayerAffiliation): string {
        switch (affiliation) {
            case PlayerAffiliation.Member:
                return '#4CAF50'; // Green for members
            case PlayerAffiliation.NonAffiliated:
                return '#FF9800'; // Orange for non-affiliated
            case PlayerAffiliation.Public:
                return '#2196F3'; // Blue for public
            default:
                return '#6B7280'; // Gray fallback
        }
    }

    getAffiliationDisplayName(affiliation: PlayerAffiliation): string {
        switch (affiliation) {
            case PlayerAffiliation.Member:
                return 'Member';
            case PlayerAffiliation.NonAffiliated:
                return 'Non Affiliated';
            case PlayerAffiliation.Public:
                return 'Public';
            default:
                return 'Unknown';
        }
    }

    private getPlayerTypeIcon(affiliation: PlayerAffiliation): string {
        switch (affiliation) {
            case PlayerAffiliation.Member:
                return 'M';
            case PlayerAffiliation.NonAffiliated:
                return 'G';
            case PlayerAffiliation.Public:
                return 'P';
            default:
                return '?';
        }
    }

    selectPlayerType(playerType: BasicPlayerType): void {
        this._priceService.getPlayerTypePrice(playerType.id, this._bookingStateService.date()).subscribe((priceData) => {
            this._bookingStateService.selectedPlayerSlot.update(slot => {

                return {
                    ...slot,
                    price: priceData,
                    playerType: playerType,
                }
            });

            this._bookingStateService.slots.update(slots => {
                const selectedIndex = this._bookingStateService.selectedPlayerSlot().index;
                const updatedSlot = {
                    ...slots[selectedIndex],
                    price: priceData,
                    playerType: playerType,
                };
                slots[selectedIndex] = updatedSlot;
                const updated = [...slots];
                return updated;
            });

            const playerTypeDrawer = this._fuseDrawerService.getComponent('playerTypeDrawer');
            
            if (playerTypeDrawer) {
                playerTypeDrawer.close();
            }

        });
    }

    isPlayerTypeSelected(playerType: BasicPlayerType): boolean {
        //return false;
        return this.selectedPlayerSlot?.playerType?.id === playerType.id;
    }
}